import { Component, OnDestroy, OnInit } from '@angular/core'
import { BreadcrumbService } from 'src/app/shared/services/breadcrumb.service'
import { MetaService } from 'src/app/shared/services/meta.service'

import { AggregationGroup } from '../../shared/interfaces/visualizations/aggregation-group.interface'
import { VisualizationService } from '../../shared/services/visualization.service'
import { ContentService } from '../../shared/services/content.service'
import {
  DynamicContent,
  DynamicContentItem
} from '../../shared/interfaces/dynamic-content.interface'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { ViewportScroller } from '@angular/common'
import { filter, Subject, takeUntil } from 'rxjs'
import { IncentiveFamilyService } from '../../shared/services/incentive-family.service'
import { IncentiveFamily } from '../../shared/enums/incentive.family'

@Component({
  selector: 'app-indicators',
  templateUrl: './indicators.component.html',
  styleUrls: ['./indicators.component.scss']
})
export class IndicatorsComponent implements OnInit, OnDestroy {
  aggregationGroups: AggregationGroup[]

  families = this.incentiveFamilyService.families;
  selectedFamily: IncentiveFamily = IncentiveFamily.RDTAX;

  loadingAggregations = false
  private fragment: string | null = null
  private readonly destroy$ = new Subject<void>()
  private aggregationLoadSequence = 0
  sections: {
    title: string
    subSections: {
      title: string
      isHalfWidth: boolean
      items: DynamicContentItem[]
    }[]
  }[]

  constructor(
    private visualizationService: VisualizationService,
    private contentService: ContentService,
    private breadcrumbService: BreadcrumbService,
    metaService: MetaService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private incentiveFamilyService: IncentiveFamilyService,
    private activatedRoute: ActivatedRoute,
  ) {
    metaService.setTags('indicators')

    this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.fragment = this.router.parseUrl(this.router.url).fragment
    })
  }

  ngOnInit(): void {
    this.breadcrumbService.breadcrumbLinks.next([
      { label: 'Indicators & analysis' }
    ])

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.selectedFamily = queryParams['dataset'] === IncentiveFamily.IPTAX
          ? IncentiveFamily.IPTAX
          : IncentiveFamily.RDTAX
        this.incentiveFamilyService.setFamily(this.selectedFamily)
        void this.loadAggregations()
      })

    this.contentService.getDynamicContent().then((dynamicContent: DynamicContent) => {
      this.sections = dynamicContent.indicatorsPageContent

      if (this.fragment) {
        setTimeout(() => {
          this.viewportScroller.scrollToAnchor(this.fragment)
        }, 0)
      }
    })
  }

  private async loadAggregations(): Promise<void> {
    const loadSequence = ++this.aggregationLoadSequence
    this.loadingAggregations = true

    this.visualizationService.listIndicatorVisualizations({
      dataset: this.selectedFamily
    }).then(
      (visualizationRes: { aggregationGroups: AggregationGroup[] }) => {
        if (loadSequence !== this.aggregationLoadSequence) return
        this.aggregationGroups = visualizationRes.aggregationGroups
        this.loadingAggregations = false
      },
      () => {
        if (loadSequence !== this.aggregationLoadSequence) return
        this.loadingAggregations = false
      }
    )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  changeFamily(family: IncentiveFamily): void {
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { dataset: family }
    })
  }
}
